import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ShoppingBag, Truck, RotateCcw, ShieldCheck } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SEO } from "@/components/common/SEO";
import { Breadcrumbs } from "@/components/common/Breadcrumbs";
import { StarRating } from "@/components/common/StarRating";
import { ProductGallery } from "@/components/product/ProductGallery";
import { ProductGrid } from "@/components/product/ProductGrid";
import { WishlistButton } from "@/components/product/WishlistButton";
import { QuickViewModal } from "@/components/product/QuickViewModal";
import { VariantPicker } from "@/components/product/VariantPicker";
import { QuantityStepper } from "@/components/product/QuantityStepper";
import { ReviewsSection } from "@/components/reviews/ReviewsSection";
import { SectionHeading } from "@/components/common/SectionHeading";
import { useProduct } from "@/hooks/useCatalog";
import { useCart } from "@/context/CartContext";
import { formatINR, salePercent } from "@/lib/format";
import { logger } from "@/lib/logger";

const RV_KEY = "elora_recently_viewed";

function pushRecentlyViewed(product) {
  try {
    const prev = JSON.parse(localStorage.getItem(RV_KEY)) || [];
    const next = [product, ...prev.filter((p) => p.handle !== product.handle)].slice(0, 8);
    localStorage.setItem(RV_KEY, JSON.stringify(next));
  } catch (err) {
    logger.warn("Could not persist recently viewed", err);
  }
}

export default function ProductPage() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { data, isLoading } = useProduct(handle);
  const product = data?.product;
  const related = data?.related || [];

  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const [quick, setQuick] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    if (product) {
      setVariant(product.variants?.[0] || null);
      setQty(1);
      setRecent((JSON.parse(localStorage.getItem(RV_KEY)) || []).filter((p) => p.handle !== product.handle));
      pushRecentlyViewed({
        handle: product.handle, title: product.title, category: product.category,
        price: product.price, compare_at_price: product.compare_at_price, on_sale: product.on_sale,
        is_new: product.is_new, available: product.available, rating: product.rating,
        review_count: product.review_count, images: product.images, variants: product.variants, options: product.options,
      });
    }
  }, [product]);

  const sale = product ? salePercent(product.price, product.compare_at_price) : 0;

  const jsonLd = useMemo(
    () =>
      product && {
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.title,
        image: product.images,
        description: product.short_description,
        category: product.category,
        offers: {
          "@type": "Offer",
          priceCurrency: "INR",
          price: product.price,
          availability: product.available ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        },
        aggregateRating: { "@type": "AggregateRating", ratingValue: product.rating, reviewCount: product.review_count },
      },
    [product]
  );

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid animate-pulse grid-cols-1 gap-12 md:grid-cols-2">
          <div className="aspect-square bg-cream" />
          <div className="space-y-4">
            <div className="h-3 w-24 bg-cream" />
            <div className="h-8 w-3/4 bg-cream" />
            <div className="h-6 w-1/3 bg-cream" />
            <div className="h-24 w-full bg-cream" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-32 text-center">
        <h1 className="font-serif text-4xl text-ink">Piece not found</h1>
        <p className="mt-3 text-muted-foreground">This piece may have been moved or is no longer available.</p>
        <Link to="/shop" className="mt-6 inline-block bg-ink px-8 py-3.5 text-[11px] uppercase tracking-widest text-ivory hover:bg-gold">
          Back to Shop
        </Link>
      </div>
    );
  }

  const handleAdd = () => addItem(product, variant?.id, qty);
  const handleBuyNow = () => {
    addItem(product, variant?.id, qty);
    navigate("/cart");
  };

  const detailSections = [
    { id: "description", label: "Description", content: product.description },
    { id: "materials", label: "Materials", content: product.details?.materials },
    { id: "dimensions", label: "Dimensions", content: product.details?.dimensions },
    { id: "care", label: "Jewellery Care", content: product.details?.care },
    { id: "shipping", label: "Shipping", content: product.details?.shipping },
    { id: "returns", label: "Returns", content: product.details?.returns },
  ].filter((s) => s.content);

  return (
    <div className="pb-24 md:pb-0">
      <SEO title={product.title} description={product.short_description} image={product.images?.[0]} jsonLd={jsonLd} />

      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <Breadcrumbs
          items={[
            { label: "Home", to: "/" },
            { label: "Shop", to: "/shop" },
            { label: product.category, to: `/shop?category=${encodeURIComponent(product.category)}` },
            { label: product.title },
          ]}
        />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:gap-16">
          {/* Gallery */}
          <ProductGallery images={product.images} title={product.title} />

          {/* Info */}
          <div className="flex flex-col">
            <p className="text-luxe text-[11px] text-gold">{product.category}</p>
            <h1 className="mt-2 font-serif text-3xl font-light text-ink sm:text-4xl">{product.title}</h1>
            <a href="#reviews" className="mt-3 flex items-center gap-3 hover:text-gold" data-testid="pdp-rating-link">
              <StarRating rating={product.rating} size={15} />
              <span className="text-sm text-muted-foreground" data-testid="pdp-rating-text">
                {product.review_count ? `${product.rating.toFixed(1)} · ${product.review_count} reviews` : "Be the first to review"}
              </span>
            </a>

            <div className="mt-5 flex items-center gap-3">
              <span className="font-sans text-2xl font-semibold text-ink">{formatINR(product.price)}</span>
              {product.compare_at_price > product.price && (
                <>
                  <span className="text-lg text-muted-foreground line-through">{formatINR(product.compare_at_price)}</span>
                  <span className="bg-gold px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">{sale}% Off</span>
                </>
              )}
            </div>

            <p className="mt-5 text-sm leading-relaxed text-muted-foreground">{product.short_description}</p>

            <VariantPicker product={product} value={variant} onChange={setVariant} className="mt-7" testId="pdp-variant-selector" />

            {/* Quantity + actions */}
            <div className="mt-7 flex items-center gap-3">
              <QuantityStepper value={qty} onChange={setQty} testIdPrefix="pdp" />
              <WishlistButton product={product} className="h-12 w-12 border border-border" />
            </div>

            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              <button
                onClick={handleAdd}
                disabled={!product.available}
                data-testid="pdp-add-to-cart-button"
                className="flex flex-1 items-center justify-center gap-2 bg-ink py-4 text-[11px] font-medium uppercase tracking-[0.18em] text-ivory transition-colors hover:bg-gold disabled:bg-muted-foreground/60"
              >
                <ShoppingBag size={15} />
                {product.available ? "Add to Bag" : "Sold Out"}
              </button>
              <button
                onClick={handleBuyNow}
                disabled={!product.available}
                data-testid="pdp-buy-now-button"
                className="flex-1 border border-ink py-4 text-[11px] font-medium uppercase tracking-[0.18em] text-ink transition-colors hover:bg-ink hover:text-ivory disabled:opacity-50"
              >
                Buy Now
              </button>
            </div>

            {/* Assurance */}
            <ul className="mt-7 space-y-2.5 border-t border-border pt-6 text-sm text-muted-foreground">
              <li className="flex items-center gap-3"><Truck size={16} className="text-gold" /> Free insured shipping across India</li>
              <li className="flex items-center gap-3"><RotateCcw size={16} className="text-gold" /> 15-day easy returns & exchanges</li>
              <li className="flex items-center gap-3"><ShieldCheck size={16} className="text-gold" /> Hallmarked & certified authenticity</li>
            </ul>

            {/* Details accordion */}
            <div className="mt-8">
              <Accordion type="single" collapsible defaultValue="description">
                {detailSections.map((s) => (
                  <AccordionItem key={s.id} value={s.id} className="border-border">
                    <AccordionTrigger className="text-luxe text-[11px] text-ink hover:no-underline">{s.label}</AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-muted-foreground">{s.content}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>

        <ReviewsSection product={product} />

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-20 border-t border-border pt-14">
            <SectionHeading eyebrow="You May Also Love" title="Related Pieces" align="left" />
            <div className="mt-10">
              <ProductGrid products={related} onQuickView={setQuick} />
            </div>
          </section>
        )}

        {/* Recently viewed */}
        {recent.length > 0 && (
          <section className="mt-20 border-t border-border pt-14">
            <SectionHeading eyebrow="Continue Browsing" title="Recently Viewed" align="left" />
            <div className="mt-10">
              <ProductGrid products={recent.slice(0, 4)} onQuickView={setQuick} />
            </div>
          </section>
        )}
      </div>

      {/* Sticky mobile add-to-cart */}
      <div className="fixed inset-x-0 bottom-0 z-30 flex items-center justify-between gap-4 border-t border-border bg-ivory/95 p-4 shadow-lg backdrop-blur-md md:hidden" data-testid="pdp-sticky-add-to-cart">
        <div>
          <p className="font-serif text-lg text-ink">{formatINR(product.price)}</p>
          {product.compare_at_price > product.price && (
            <p className="text-xs text-muted-foreground line-through">{formatINR(product.compare_at_price)}</p>
          )}
        </div>
        <button
          onClick={handleAdd}
          disabled={!product.available}
          className="flex flex-1 items-center justify-center gap-2 bg-ink py-3.5 text-[11px] font-medium uppercase tracking-[0.18em] text-ivory hover:bg-gold disabled:bg-muted-foreground/60"
        >
          <ShoppingBag size={15} /> {product.available ? "Add to Bag" : "Sold Out"}
        </button>
      </div>

      <QuickViewModal product={quick} open={!!quick} onOpenChange={(o) => !o && setQuick(null)} />
    </div>
  );
}
